<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import PostSaleAgendaCalendar from './PostSaleAgendaCalendar.vue';
import PostSaleAgendaDayList from './PostSaleAgendaDayList.vue';
import PostSaleAgendaFilters from './PostSaleAgendaFilters.vue';
import PostSaleAgendaListHeader from './PostSaleAgendaListHeader.vue';
import PostSaleAgendaSummary from './PostSaleAgendaSummary.vue';
import { usePostSaleAgenda } from '../composables/usePostSaleAgenda';

const props = defineProps({
  navigationCollapsed: { type: Boolean, default: false },
  responsibles: { type: Array, default: () => [] },
});

const emit = defineEmits(['action', 'open']);
const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');
const agenda = usePostSaleAgenda({ t, notify: useAlert });
const userName = computed(() => currentUser.value?.name?.trim() || '');
const canSelectResponsible = computed(() =>
  ['administrator', 'agent', 'post_sale_manage'].some(permission =>
    currentAccount.value?.permissions?.includes(permission)
  )
);

const selectedDayLabel = computed(() => {
  const value = new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  }).format(agenda.selectedDate.value);

  return value.replace(
    / de ([^,]+)/,
    (_, month) => ` de ${month.charAt(0).toUpperCase()}${month.slice(1)}`
  );
});
const refreshLabel = computed(() =>
  agenda.minutesSinceRefresh.value === null
    ? ''
    : t('POST_SALE.AGENDA.UPDATED_AT', {
        minutes: agenda.minutesSinceRefresh.value,
      })
);
const countItems = computed(() => [
  { id: 'amarelos', label: t('POST_SALE.DEADLINES.AMARELOS') },
  { id: 'laranjas', label: t('POST_SALE.DEADLINES.LARANJAS') },
  { id: 'vermelhos', label: t('POST_SALE.DEADLINES.VERMELHOS') },
]);
const selectedCountItems = computed(() =>
  countItems.value.filter(item => agenda.selectedCounts.value[item.id])
);
const selectedReferenceDateLabel = computed(() => {
  const [weekday, date] = selectedDayLabel.value.split(', ');

  return date && weekday ? `${date}, ${weekday}` : selectedDayLabel.value;
});
const selectedFooterDateLabel = selectedReferenceDateLabel;
const selectedCountLabel = (bucket, count) => {
  if (bucket !== 'vermelhos')
    return countItems.value.find(item => item.id === bucket)?.label;

  if (count === 1) return t('POST_SALE.AGENDA.SELECTED_COUNTS.OVERDUE_ONE');

  return t('POST_SALE.AGENDA.SELECTED_COUNTS.OVERDUE_OTHER');
};
const countClass = bucket =>
  ({
    amarelos: 'bg-[#FFF7E5] text-[#D69500]',
    laranjas: 'bg-[#FFF1E5] text-[#FF7A11]',
    vermelhos: 'bg-[#FCE9E9] text-[#E33838]',
  })[bucket];
const countTextClass = bucket =>
  ({
    amarelos: 'text-[#FFC43E]',
    laranjas: 'text-[#FF7A11]',
    vermelhos: 'text-[#E33838]',
  })[bucket];
const footerWidthClass = computed(() =>
  props.navigationCollapsed
    ? 'w-full md:w-[calc(100vw-106px)] lg:w-[calc(100vw-186px)]'
    : 'w-full md:w-[calc(100vw-295px)]'
);

onMounted(agenda.load);
</script>

<template>
  <div class="grid gap-5">
    <PostSaleAgendaCalendar
      :days="agenda.days.value"
      :loading="agenda.loading.value"
      :month-value="agenda.monthValue.value"
      :refresh-label="refreshLabel"
      :selected-date="agenda.selectedDate.value"
      @next-month="agenda.nextMonth"
      @previous-month="agenda.previousMonth"
      @refresh="agenda.load"
      @select-date="agenda.selectDate"
      @select-month="agenda.selectMonth"
      @today="agenda.goToToday"
    />

    <PostSaleAgendaSummary
      :counts="agenda.todayCounts.value"
      :user-name="userName"
    />

    <section class="grid gap-2 pb-14">
      <PostSaleAgendaFilters
        :address-filters="agenda.addressFilters.value"
        :address-options="agenda.addressOptions.value"
        :addresses-loading="agenda.loadingAddresses.value"
        @search-addresses="agenda.loadAddresses"
        @set-address="agenda.setAddress"
      />
      <section
        data-testid="post-sale-agenda-process-panel"
        class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
      >
        <PostSaleAgendaListHeader
          :bucket-filters="agenda.enabledBuckets.value"
          :can-select-responsible="canSelectResponsible"
          :responsible-user-id="agenda.responsibleUserId.value"
          :responsibles="props.responsibles"
          :selected-day-label="selectedReferenceDateLabel"
          @set-responsible="agenda.setResponsible"
          @toggle-bucket="agenda.toggleBucket"
        />

        <PostSaleAgendaDayList
          :cases="agenda.selectedCases.value"
          :loading="agenda.loading.value"
          @action="emit('action', $event)"
          @open="emit('open', $event)"
        />
      </section>

      <footer
        data-testid="post-sale-agenda-footer"
        class="fixed bottom-0 right-0 z-20 flex h-14 items-center justify-center gap-[25px] overflow-x-hidden bg-white px-4"
        :class="footerWidthClass"
      >
        <p class="shrink-0 text-sm text-[#74848B]">
          {{ selectedFooterDateLabel }}
        </p>
        <div
          v-for="item in selectedCountItems"
          :key="item.id"
          class="flex shrink-0 items-center gap-2 text-sm"
        >
          <span
            class="rounded-full px-2 py-1 text-xs font-bold"
            :class="countClass(item.id)"
          >
            {{ agenda.selectedCounts.value[item.id] }}
          </span>
          <span :class="countTextClass(item.id)">
            {{
              selectedCountLabel(item.id, agenda.selectedCounts.value[item.id])
            }}
          </span>
        </div>
      </footer>
    </section>
  </div>
</template>
